import { useSelector } from 'react-redux';
import { useZeny } from './ZenyProvider';
import { occurredAt } from './issueModel';

// How many urgent exceptions have arrived since this person last looked at them.
//
// The rail used to print the same number twice: Today carried the count of urgent open exceptions,
// and the bell beside it carried that same count. Two places, one figure, and no way to tell whether
// anything had changed since you last dealt with them - so the bell said thirty all day while you
// worked through them, and said thirty again when a new one arrived.
//
// They answer different questions. Today answers "how much is outstanding", which is a state and
// belongs with the work. The bell answers "has anything happened since I looked", which is an event
// and goes to zero when you look. Kept in this browser, per account, because it is a property of
// this person's attention rather than of the plant.
const key = (user) => `alerts-seen.${user?.tenantId || 'unknown'}.${user?.id || 'anonymous'}`;

export const markAlertsRead = (user) => {
  try {
    window.localStorage.setItem(key(user), new Date().toISOString());
  } catch (error) {
    // A browser that refuses storage simply keeps showing the count; nothing else depends on it.
  }
};

const lastRead = (user) => {
  try {
    return Date.parse(window.localStorage.getItem(key(user)) || '') || 0;
  } catch (error) {
    return 0;
  }
};

export default function useUnreadAlerts() {
  const user = useSelector((state) => state.session.user);
  const { urgent, error, updatedAt } = useZeny();
  if (error || !updatedAt) return 0;
  const since = lastRead(user);
  // Before anybody has opened the drawer there is nothing to be "since", so the first visit shows
  // what is urgent rather than claiming everything is new.
  return urgent.filter((issue) => !since || Date.parse(occurredAt(issue) || 0) > since).length;
}
